import { useEffect } from 'react';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import {
  InboxMessageAddedDocument,
  InboxMessageAddedSubscription,
  InboxMessageAddedSubscriptionVariables,
  PaginationSortOrderEnum,
  useGetInboxMessageThreadLazyQuery,
  useSessionQuery,
} from '@src/graphql/generated';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { QueryParams } from '../../interfaces/queryParams.interface';
import { UseMessageThreadParams, UseMessageThreadValue } from './interfaces/useMessageThread.interface';

export const useMessageThread = ({ onCompleted }: UseMessageThreadParams | undefined = {}): UseMessageThreadValue => {
  const { queryParams } = useQueryParams<QueryParams>();

  const { data: sessionData } = useSessionQuery();

  const recruiter = sessionData?.currentRecruiter;

  const [
    fetchMessageThread,
    {
      data: messageThreadData,
      refetch: refetchMessageThread,
      loading: isMessageThreadLoading,
      subscribeToMore: subscribeToMoreMessageThread,
    },
  ] = useGetInboxMessageThreadLazyQuery({
    onCompleted: onCompleted,
  });

  useEffect(() => {
    let unsubscribeToMoreMessageThread: undefined | (() => void);

    const initiateMessages = async () => {
      if (!queryParams.threadId) {
        return;
      }

      if (!messageThreadData) {
        await fetchMessageThread({
          fetchPolicy: 'network-only',
          variables: {
            messageThreadId: queryParams.threadId,
            messagesPaginationOptions: {
              sortOrder: PaginationSortOrderEnum.ASC,
            },
            recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
          },
        });
      } else {
        await refetchMessageThread({
          messageThreadId: queryParams.threadId,
          messagesPaginationOptions: {
            sortOrder: PaginationSortOrderEnum.ASC,
          },
          recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
        });
      }

      unsubscribeToMoreMessageThread = subscribeToMoreMessageThread<
        InboxMessageAddedSubscription,
        InboxMessageAddedSubscriptionVariables
      >({
        document: InboxMessageAddedDocument,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          }

          const newMessage = subscriptionData.data.messageAdded;

          const doesMessageExist = prev?.messageThread?.messages.result.some(message => {
            return message?.id === newMessage.id;
          });

          if (prev.messageThread.id !== queryParams.threadId || doesMessageExist) {
            return prev;
          }

          return {
            ...prev,
            messageThread: {
              ...prev.messageThread,
              messages: {
                ...prev.messageThread.messages,
                result: [...prev.messageThread.messages.result, newMessage],
              },
            },
          };
        },
        variables: {
          messageThreadId: queryParams.threadId,
        },
      });
    };

    initiateMessages();

    return () => {
      if (queryParams.threadId) {
        unsubscribeToMoreMessageThread?.();
      }
    };
  }, [queryParams.threadId]);

  return {
    isMessageThreadLoading: isMessageThreadLoading,
    messageThreadData: messageThreadData,
    refetchMessageThread: refetchMessageThread,
  };
};
